import { cn } from "@core-ui/utils/cn";
import t from "@ext/localization/locale/translate";
import type { CommentInputProps } from "@ext/markdown/elements/comment/edit/components/Popover/CommentInput";
import type { Editor } from "@tiptap/react";
import { Button, IconButton, ProgressIconButton } from "@ui-kit/Button";
import { Tooltip, TooltipContent, TooltipShortcut, TooltipTrigger } from "@ui-kit/Tooltip";
import { memo, type ReactElement, useEffect, useState } from "react";

interface CommentConfirmButtonProps extends Pick<CommentFooterProps, "onConfirm" | "editor" | "editable"> {
	isNewComment?: boolean;
	disabled?: boolean;
}

interface CommentFooterProps extends Pick<CommentInputProps, "onConfirm" | "onCancel"> {
	isNewComment?: boolean;
	editable?: boolean;
	editor: Editor;
}

const CONFIRM_SHORTCUT = "Mod-Enter";
const CANCEL_SHORTCUT = "Esc";

const WithTooltip = ({ text, hotKey, children }: { text: string; hotKey: string; children: ReactElement }) => (
	<Tooltip>
		<TooltipContent sideOffset={2}>
			<div className="flex items-center gap-2">
				{text}
				<TooltipShortcut className="p-0" inverse value={hotKey} />
			</div>
		</TooltipContent>
		<TooltipTrigger asChild>{children}</TooltipTrigger>
	</Tooltip>
);

const CommentConfirmButton = ({ isNewComment, editor, onConfirm, disabled }: CommentConfirmButtonProps) => {
	if (!isNewComment) {
		return (
			<WithTooltip hotKey={CONFIRM_SHORTCUT} text={t("save")}>
				<Button
					disabled={disabled}
					onClick={() => onConfirm(editor.getJSON().content)}
					size="xs"
					variant="primary"
				>
					{t("save")}
				</Button>
			</WithTooltip>
		);
	}

	return (
		<IconButton
			className="h-auto rounded-lg"
			disabled={disabled}
			icon="arrow-up"
			iconClassName="h-4 w-4"
			onClick={() => onConfirm(editor.getJSON().content)}
			size="xs"
		/>
	);
};

const CommentCancelButton = ({ onCancel, isNewComment }: { onCancel: () => void; isNewComment: boolean }) => {
	if (!isNewComment) {
		return (
			<WithTooltip hotKey={CANCEL_SHORTCUT} text={t("cancel")}>
				<Button onClick={onCancel} size="xs" variant="outline">
					{t("cancel")}
				</Button>
			</WithTooltip>
		);
	}

	return (
		<WithTooltip hotKey={CANCEL_SHORTCUT} text={t("cancel")}>
			<ProgressIconButton icon="x" onClick={onCancel} />
		</WithTooltip>
	);
};

export const CommentFooter = memo((props: CommentFooterProps) => {
	const { onConfirm, onCancel, editor, isNewComment = true, editable } = props;
	const [disabled, setDisabled] = useState(isNewComment);

	useEffect(() => {
		if (!editor || editor.isDestroyed) return;

		const onUpdate = ({ editor }: { editor: Editor }) => {
			setDisabled(editor.isEmpty);
		};

		editor.on("update", onUpdate);
		return () => {
			editor.off("update", onUpdate);
		};
	}, [editor]);

	const isOldNotEditableComment = !isNewComment && !editable;

	return (
		<div
			className={cn(
				"flex items-center gap-1.5 ml-auto transition-[height,opacity]",
				!isNewComment && "w-full justify-end",
				!isNewComment && editable && "mt-3",
				isOldNotEditableComment && "overflow-hidden",
			)}
			style={{
				alignSelf: "end",
				...(!isNewComment
					? {
							height: isOldNotEditableComment ? "0" : "1.75rem",
							opacity: isOldNotEditableComment ? "0" : "1",
							pointerEvents: isOldNotEditableComment ? "none" : "auto",
						}
					: {}),
			}}
		>
			{onCancel && <CommentCancelButton isNewComment={isNewComment} onCancel={onCancel} />}
			<CommentConfirmButton
				disabled={disabled}
				editor={editor}
				isNewComment={isNewComment}
				onConfirm={onConfirm}
			/>
		</div>
	);
});
